import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PerformanceTable from "./PerformanceTable";
import { Box, Grid } from "@mui/system";
import SquareIcon from "@mui/icons-material/Square";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  selectBizDashboardLoading,
  selectBizOutputMetadata,
  selectBizQueryOutput,
  selectBizComparisonQueryOutput,
  selectBizComparisonLoading,
  selectBizIsPerformanceApiCalled,
  selectBizPerformanceBrandSearch,
  setIsPerformanceApiCalled,
  setPerformanceBrandSearch,
} from "../../../../../features/bizDevelopmentDashboardSlice";
import { useBizDevelopmentDashboard } from "../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import {
  buildColumns,
  exportToCSV,
} from "../../helpers/bizDevelopmentDashboardHelpers";
import FloatingActionItems from "../../../../../pages/FloatingItems/FloatingActionItems";

interface TimeframeOption {
  value: string;
  label: string;
}

const Performance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const queryOutput = useSelector(selectBizQueryOutput);
  const outputMetadata = useSelector(selectBizOutputMetadata);
  const isLoading = useSelector(selectBizDashboardLoading);
  const comparisonQueryOutput = useSelector(selectBizComparisonQueryOutput);
  const isComparisonLoading = useSelector(selectBizComparisonLoading);
  const isPerformanceApiCalled = useSelector(selectBizIsPerformanceApiCalled);
  const {
    fetchDashboardData,
    fetchComparisonData,
    clearComparisonData,
    getSitesFromParams,
  } = useBizDevelopmentDashboard();

  const fmt = (d: dayjs.Dayjs) => d.format("DD/MM/YYYY");

  const timeframeOptions = useMemo(
    (): TimeframeOption[] => [
      {
        value: "yesterday",
        label: `Yesterday (${fmt(dayjs().subtract(1, "day"))})`,
      },
      {
        value: "two_days_ago",
        label: `2 Days Ago (${fmt(dayjs().subtract(2, "day"))})`,
      },
      {
        value: "three_days_ago",
        label: `3 Days Ago (${fmt(dayjs().subtract(3, "day"))})`,
      },
      { value: "mtd", label: "MTD" },
    ],
    [],
  );

  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>(
    timeframeOptions.find(
      (o) =>
        o.value === (searchParams.get("timeframePerformance") ?? "yesterday"),
    ) ?? timeframeOptions[0],
  );

  const [selectedAdditional, setSelectedAdditional] =
    useState<TimeframeOption | null>(() => {
      const saved = searchParams.get("additionalTimeframePerformance");
      return saved
        ? (timeframeOptions.find((o) => o.value === saved) ?? null)
        : null;
    });

  const additionalTimeframeOptions = useMemo((): TimeframeOption[] => {
    const all: TimeframeOption[] = [
      {
        value: "yesterday",
        label: `Yesterday (${fmt(dayjs().subtract(1, "day"))})`,
      },
      {
        value: "two_days_ago",
        label: `2 Days Ago (${fmt(dayjs().subtract(2, "day"))})`,
      },
      {
        value: "three_days_ago",
        label: `3 Days Ago (${fmt(dayjs().subtract(3, "day"))})`,
      },
    ];
    return all.filter((o) => o.value !== selectedTimeframe.value);
  }, [selectedTimeframe]);

  useEffect(() => {
    if (isPerformanceApiCalled) return;
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    fetchDashboardData(selectedTimeframe.value, sites);
    dispatch(setIsPerformanceApiCalled(true));
    if (selectedAdditional) {
      fetchComparisonData(selectedAdditional.value, sites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerformanceApiCalled]);

  const handleTimeframeChange = (_: any, newValue: TimeframeOption | null) => {
    if (!newValue) return;
    setSelectedTimeframe(newValue);
    setSelectedAdditional(null);
    clearComparisonData();
    dispatch(setPerformanceBrandSearch(""));
    setSearchParams((prev) => {
      prev.set("timeframePerformance", newValue.value);
      prev.delete("additionalTimeframePerformance");
      prev.delete("brandSearchPerformance");
      return prev;
    });
    fetchDashboardData(newValue.value, getSitesFromParams());
    dispatch(setIsPerformanceApiCalled(true));
  };

  const handleAdditionalChange = (_: any, newValue: TimeframeOption | null) => {
    setSelectedAdditional(newValue);
    setSearchParams((prev) => {
      if (newValue) {
        prev.set("additionalTimeframePerformance", newValue.value);
      } else {
        prev.delete("additionalTimeframePerformance");
      }
      return prev;
    });
    if (newValue) {
      fetchComparisonData(newValue.value, getSitesFromParams());
    } else {
      clearComparisonData();
    }
  };

  const brandSearch = useSelector(selectBizPerformanceBrandSearch);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  // Initialise Redux from URL param on first mount (handles page refresh)
  useEffect(() => {
    const saved = searchParams.get("brandSearchPerformance");
    if (saved && saved !== brandSearch) {
      dispatch(setPerformanceBrandSearch(saved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brandNameOptions = useMemo(
    () =>
      Array.from(
        new Set(
          queryOutput
            .map((r: Record<string, any>) => String(r.brand ?? ""))
            .filter(Boolean),
        ),
      ).sort(),
    [queryOutput],
  );

  const filteredQueryOutput = useMemo(() => {
    if (!brandSearch.trim()) return queryOutput;
    const q = brandSearch.trim().toLowerCase();
    return queryOutput.filter((r: Record<string, any>) =>
      String(r.brand ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [queryOutput, brandSearch]);

  const filteredComparisonOutput = useMemo(() => {
    if (!brandSearch.trim()) return comparisonQueryOutput;
    const q = brandSearch.trim().toLowerCase();
    return (comparisonQueryOutput ?? []).filter((r: Record<string, any>) =>
      String(r.brand ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [comparisonQueryOutput, brandSearch]);

  const isMtd = searchParams.get("timeframePerformance") === "mtd";

  const handleDownload = () => {
    const columns = buildColumns(outputMetadata, isMtd);

    exportToCSV({
      columns,
      primaryRows: queryOutput,
      comparisonRows: selectedAdditional ? (comparisonQueryOutput ?? []) : [],
      primaryLabel: selectedTimeframe.label,
      comparisonLabel: selectedAdditional?.label ?? "",
      isMtd,
      filename: `performance_${selectedTimeframe.value}${selectedAdditional ? `_vs_${selectedAdditional.value}` : ""}`,
    });
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center" sx={{ p: 2 }}>
        {/* Brand Name — left */}
        <Grid size={3}>
          <Autocomplete
            freeSolo
            forcePopupIcon
            disabled={
              queryOutput.length === 0 || isLoading || isComparisonLoading
            }
            open={brandDropdownOpen}
            onOpen={() => setBrandDropdownOpen(true)}
            onClose={() => setBrandDropdownOpen(false)}
            options={brandNameOptions}
            inputValue={brandSearch}
            onInputChange={(_, val, reason) => {
              dispatch(setPerformanceBrandSearch(val));
              setSearchParams((prev) => {
                if (val) prev.set("brandSearchPerformance", val);
                else prev.delete("brandSearchPerformance");
                return prev;
              });
              if (reason === "input") setBrandDropdownOpen(false);
            }}
            onChange={(_, val) => {
              const v = typeof val === "string" ? val : "";
              dispatch(setPerformanceBrandSearch(v));
              setBrandDropdownOpen(false);
              setSearchParams((prev) => {
                if (v) prev.set("brandSearchPerformance", v);
                else prev.delete("brandSearchPerformance");
                return prev;
              });
            }}
            size="small"
            sx={{ pt: "8px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                color="secondary"
                label="Brand Name"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: "rgba(0,0,0,0.54)", ml: "12px" }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* Spacer — pushes the timeframe controls to the right */}
        <Grid size="grow" />

        {/* Time Frame */}
        <Grid size={3}>
          <Autocomplete
            disablePortal
            disabled={isLoading || isComparisonLoading}
            options={timeframeOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={selectedTimeframe}
            onChange={handleTimeframeChange}
            fullWidth
            size="small"
            sx={{ pt: "8px" }}
            renderInput={(params) => (
              <TextField
                color="secondary"
                {...params}
                required
                placeholder="Search Time Frame or choose from the list"
                label="Time Frame"
                data-tracker="799"
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Autocomplete
            disablePortal
            disabled={
              selectedTimeframe.value === "mtd" ||
              isLoading ||
              isComparisonLoading ||
              queryOutput.length === 0
            }
            options={additionalTimeframeOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={selectedAdditional}
            onChange={handleAdditionalChange}
            fullWidth
            size="small"
            sx={{ pt: "8px" }}
            renderInput={(params) => (
              <TextField
                data-tracker="800"
                color="secondary"
                {...params}
                placeholder="Compare"
                label="Additional Time Frame"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AddIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size="auto">
          <Box display={"flex"} justifyContent={"flex-end"}>
            <IconButton
              aria-label="refresh"
              color="secondary"
              disabled={isLoading || isComparisonLoading}
              onClick={() => {
                const sites = getSitesFromParams();
                fetchDashboardData(selectedTimeframe.value, sites);
                dispatch(setIsPerformanceApiCalled(true));
                if (selectedAdditional) {
                  fetchComparisonData(selectedAdditional.value, sites);
                }
              }}
              data-tracker="802"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              color="secondary"
              sx={{ ml: 2 }}
              disabled={
                isLoading || isComparisonLoading || queryOutput.length === 0
              }
              onClick={handleDownload}
              data-tracker="801"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Grid>
        {selectedAdditional && (
          <Grid size={12}>
            <Box display={"flex"} alignItems={"center"}>
              <Typography
                display={"flex"}
                alignItems={"center"}
                fontSize={"12px"}
                mr={2}
              >
                <SquareIcon
                  sx={{
                    color: "#000000DE",
                    mr: 0.2,
                    fontSize: "20px",
                    borderRadius: "16px",
                  }}
                />
                {selectedTimeframe.label}
              </Typography>
              {selectedAdditional && (
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  fontSize={"12px"}
                >
                  <SquareIcon
                    sx={{
                      color: "#00000061",
                      mr: 0.2,
                      fontSize: "20px",
                      borderRadius: "16px",
                    }}
                  />
                  {selectedAdditional.label}
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      <PerformanceTable
        outputMetadata={outputMetadata}
        queryOutput={filteredQueryOutput}
        isLoading={isLoading}
        comparisonQueryOutput={
          selectedAdditional ? filteredComparisonOutput : []
        }
        isComparisonLoading={isComparisonLoading}
        isComparisonActive={selectedAdditional !== null}
      />
      <div data-tracker="803">
        <FloatingActionItems
          selectedSiteFloatingAction={getSitesFromParams().join(",")}
          selectedDimensionsFloatingAction=""
          trackIdFloatingAction="804"
          disabledButton={queryOutput.length === 0}
        />
      </div>
    </>
  );
};

export default Performance;
